import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Header from "../../../components/Header";
import HomeButton from "../../../components/HomeButton";
import SpinnerLoader from "../../../components/SpinnerLoader";
import TabNavbar from "../../../components/Tabbar";
import Select from "react-select";
import { RootState, useAppDispatch } from "../../../redux/store";
import { HR_TABS } from "../../../utils/TabbarData";
import { useLocation, useNavigate } from "react-router-dom";
import { getCourses } from "../../../redux/slices/hr.applications.slice";
import CourseCard from "../../../components/Card/CourseCard";
import { setSelectedCourseId } from "../../../redux/slices/hr.applications.slice";
import { useTranslation } from "react-i18next";
import { BsFilter } from "react-icons/bs";
import Pagination from "../../../components/Pagination";
import { extractCareerLevelNumber } from "../../../utils/helper";

function AllTrainings() {
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [typeFilter, setTypeFilter] = useState(""); // Local / Group or empty
  const [minJobLevelFilter, setMinJobLevelFilter] = useState(""); // number input as string
  const [minJobLevelOperator, setMinJobLevelOperator] = useState("="); // '=', '>', '<'
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [minJobLevelError, setMinJobLevelError] = useState("");
  const [isRestored, setIsRestored] = useState(false);

  const [selectedCourseNames, setSelectedCourseNames] = useState<string[]>([]);

  const [debouncedMinJobLevelFilter, setDebouncedMinJobLevelFilter] =
    useState(minJobLevelFilter);
  const [inputValue, setInputValue] = useState(searchTerm);
  useEffect(() => {
    setInputValue(searchTerm); // sync on prop change
  }, [searchTerm]);

  const { courseResp, courses, loading } = useSelector(
    (state: RootState) => state.hrApplications
  );

  const [filteredCourses, setFilteredCourses] = useState<any[]>(courses);
  const totalPages =
    filteredCourses.length > 0
      ? Math.ceil(filteredCourses?.length / itemsPerPage)
      : 1;

  const paginatedData = filteredCourses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // // const handlePageChange = (page: number) => setCurrentPage(page);
  // const handleSearchChange = (value: string) => {
  //   setSearchTerm(value);
  //   setCurrentPage(1);
  // };

  const handleNavigateToCourseSessions = (id: number | string) => {
    const courseById = courses.find((course) => course.course_id === id);
    if (!courseById) return;
    dispatch(setSelectedCourseId(courseById));
    sessionStorage.setItem(
      "allTrainingFilters",
      JSON.stringify({
        currentPage,
        searchTerm,
        typeFilter,
        minJobLevelFilter,
        minJobLevelOperator,
        itemsPerPage,
      })
    );

    navigate("/hr/all-training/course-applications/" + id);
  };

  const _courses = courses
    .map((course: any) => ({
      course_id: course.course_id,
      course_name: course.course_name,
    }))
    .filter(
      (course: any, index: any, self: any) =>
        index ===
        self.findIndex((t: any) => t.course_name === course.course_name)
    );

  const courseOptions = _courses.map((course: any) => ({
    label: course.course_name,
    value: course.course_name,
  }));

  const location = useLocation();
  useEffect(() => {
    const isComingBackFromCourse =
      location.state?.from === "CourseApplications";
    if (isComingBackFromCourse) {
      const savedState = JSON.parse(
        sessionStorage.getItem("allTrainingFilters") || "{}"
      );
      if (savedState) {
        setCurrentPage(savedState.currentPage || 1);
        setSearchTerm(savedState.searchTerm || "");
        setTypeFilter(savedState.typeFilter || "");
        setMinJobLevelFilter(savedState.minJobLevelFilter || "");
        setMinJobLevelOperator(savedState.minJobLevelOperator || "=");
        setItemsPerPage(savedState.itemsPerPage || 12);
      }
    } else {
      // Reset if coming from other pages
      sessionStorage.removeItem("allTrainingFilters");
    }
    setIsRestored(true); // Only after session restore or reset
  }, [location]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedMinJobLevelFilter(minJobLevelFilter);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [minJobLevelFilter]);

  const applyFilters = () => {
    const isComingBackFromCourse =
      location.state?.from === "CourseApplications";
    let filteredData = courses;

    if (selectedCourseNames.length > 0) {
      filteredData = filteredData.filter((course: any) =>
        selectedCourseNames.includes(course.course_name)
      );
    }

    // Filter by Type (category_type)
    if (typeFilter !== "") {
      filteredData = filteredData.filter(
        (course: any) => course.category_type === typeFilter.toUpperCase()
      );
    }

    // Filter by Min Career Level (numeric compare)
    if (minJobLevelFilter.trim() !== "") {
      const level = parseInt(minJobLevelFilter);
      if (!isNaN(level)) {
        filteredData = filteredData.filter((course: any) => {
          const courseLevel = parseInt(
            extractCareerLevelNumber(course.minimum_job_level)
          );
          if (isNaN(courseLevel)) return false;
          switch (minJobLevelOperator) {
            case "=":
              return courseLevel === level;
            case ">":
              return courseLevel > level;
            case "<":
              return courseLevel < level;
            case ">=":
              return courseLevel >= level;
            case "<=":
              return courseLevel <= level;
            default:
              return true;
          }
        });
      }
    }
    const savedState = JSON.parse(
      sessionStorage.getItem("availableTrainingsFilters") || "{}"
    );
    setFilteredCourses(filteredData);
    if (isComingBackFromCourse) {
      if (savedState) {
        setCurrentPage(savedState.currentPage || 1);
        setItemsPerPage(savedState.itemsPerPage || 12);
      }
    } else {
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    dispatch(getCourses({}));
  }, [dispatch]);

  useEffect(() => {
    setFilteredCourses(courses);
  }, [courses]);

  useEffect(() => {
    applyFilters();
  }, [
    // searchTerm,
    typeFilter,
    debouncedMinJobLevelFilter,
    minJobLevelOperator,
    courses,
    selectedCourseNames,
  ]);

  const isMobileOrTablet = window.innerWidth < 1024; // Checking for mobile/tablet screens

  return (
    <div className="flex flex-col items-start gap-8">
      {!isMobileOrTablet && <HomeButton />}
      {isMobileOrTablet ? (
        <>
          {" "}
          <TabNavbar
            id=""
            tabs={HR_TABS}
            selectedTabId={1}
            type="Priority Requests"
          />
          <HomeButton />
          <Header />
        </>
      ) : (
        <>
          <Header />
          <TabNavbar
            id=""
            tabs={HR_TABS}
            selectedTabId={1}
            type="Priority Requests"
          />{" "}
        </>
      )}

      <div className="w-full h-full">
        <div
          className={`${
            isMobileOrTablet ? "flex flex-col" : "flex flex-row"
          } gap-4 w-full`}
        >
          {!isMobileOrTablet && (
            <button
              onClick={() => setIsFilterOpen(true)}
              className="px-4 py-2 bg-[#961e38] rounded hover:bg-[#961e38] text-white w-[8vw]"
            >
              {t("Filter")}
            </button>
          )}

          {/* Search and Filter */}
          <div className="w-full flex justify-between items-center gap-2">
            {isMobileOrTablet && (
              <div>
                <button
                  onClick={() => setIsFilterOpen(true)}
                  className="px-4 py-2 bg-[#961e38] rounded hover:bg-[#961e38] text-white"
                >
                  <BsFilter className="inline" />
                </button>
              </div>
            )}
          </div>

          {/* Filter Widget */}
          {isFilterOpen && (
            <div
              className={`absolute ${
                isMobileOrTablet ? "top-[41%]" : "top-[41%] left-[20%]"
              }  mt-2 bg-white shadow-lg rounded border w-80 p-4 z-50`}
              style={{ minWidth: "300px" }}
            >
              {/* Close button */}
              <button
                className="absolute top-2 right-2 text-gray-600 hover:text-gray-900"
                onClick={() => setIsFilterOpen(false)}
              >
                &#10005;
              </button>

              <div className="flex flex-col gap-2">
                <label>{t("Course Name")}</label>
                <Select
                  isMulti
                  options={courseOptions}
                  value={courseOptions.filter((opt) =>
                    selectedCourseNames.includes(opt.value)
                  )}
                  onChange={(selected) => {
                    const selectedValues = selected.map((opt) => opt.value);
                    setSelectedCourseNames(selectedValues);
                  }}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder={t("Select...")}
                  noOptionsMessage={() => t("No options")}
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      maxHeight: "200px",
                      overflowY: "auto",
                      scrollbarWidth: "none", // For Firefox
                      msOverflowStyle: "none", // For IE and Edge
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      maxHeight: "120px",
                      overflowY: "auto",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none", // For IE and Edge
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      maxWidth: "100%",
                    }),
                  }}
                />

                {/* TYPE Dropdown */}
                <label>{t("Type")}</label>
                <select
                  className="border p-2 rounded"
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    // setLanguageFilter("");
                    // setMinJobLevelFilter("");
                    // setMinJobLevelOperator("=");
                  }}
                >
                  <option value="">{t("All")}</option>
                  <option value="Local">{t("Local")}</option>
                  <option value="Group">{t("Group")}</option>
                </select>

                {/* Min Career Level with Operator */}
                <label>{t("Min Career Level")}</label>
                <div className="w-full">
                  <div className="flex gap-2">
                    <select
                      className="border p-2 rounded"
                      value={minJobLevelOperator}
                      onChange={(e) => {
                        setMinJobLevelOperator(e.target.value);
                        // setTypeFilter("");
                        // setLanguageFilter("");
                      }}
                    >
                      <option value="=">=</option>
                      <option value=">">{">"}</option>
                      <option value="<">{"<"}</option>
                      <option value=">=">{">="}</option>
                      <option value="<=">{"<="}</option>
                    </select>
                    <input
                      style={{ width: "120px" }}
                      type="number"
                      className="border p-2 rounded"
                      placeholder={t("Enter level")}
                      value={minJobLevelFilter}
                      onChange={(e) => {
                        const val = e.target.value;
                        // setMinJobLevelFilter(val);

                        // Validation
                        const numVal = Number(val);
                        if (val === "") {
                          setMinJobLevelError(""); // no error if empty
                          setMinJobLevelFilter(val);
                        } else if (isNaN(numVal) || numVal < 1 || numVal > 20) {
                          setMinJobLevelError(
                            t("Please enter a number between 1 and 20")
                          );
                        } else {
                          setMinJobLevelError("");
                          setMinJobLevelFilter(val);
                        }
                      }}
                    />
                  </div>
                  {minJobLevelError && (
                    <p className="text-red-600 text-sm mt-1">
                      {minJobLevelError}
                    </p>
                  )}
                </div>

                <button
                  className="mt-2 text-sm bg-[#961e38] text-white py-2 px-4 rounded"
                  onClick={() => {
                    setSelectedCourseNames([]);
                    setTypeFilter("");
                    setMinJobLevelFilter("");
                    setMinJobLevelOperator("=");
                  }}
                >
                  {t("Clear Filters")}
                </button>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center w-full justify-center">
            <SpinnerLoader />
          </div>
        ) : (
          <div className="flex flex-col w-full justify-start items-start gap-8 px-10 mt-10">
            {paginatedData.length > 0 ? (
              paginatedData.map((item) => (
                <>
                  <CourseCard
                    handleNavigateToCourseSessions={
                      handleNavigateToCourseSessions
                    }
                    item={item}
                  />
                </>
              ))
            ) : (
              <div className="flex items-center w-full justify-center">
                <p className="text-2xl text-[#961e38] font-semibold">
                  {t("No Courses found")}
                </p>
              </div>
            )}
            {/* Pagination Controls */}
            {courses.length > 0 && !loading && (
              <Pagination
                isCoursePage={true}
                totalPages={totalPages}
                total_item_count={filteredCourses.length}
                _currentPage={currentPage}
                _itemsPerPage={itemsPerPage}
                handleChange={({ page, itemsPerPage }) => {
                  setCurrentPage(page);
                  setItemsPerPage(itemsPerPage);
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AllTrainings;
